/**
* Account Management Module
* Handles user authentication, registration, login, and user dashboard functionality
*/

(function()
{
    'use strict';

    // Global account state
    let currentUser = null;
    let authToken = null;

    // Track password reset request to prevent duplicate submissions
    let passwordResetInProgress = false;

    /**
    * Check if necessary cookies are allowed
    */
    function isNecessaryCookiesAllowed()
    {
        // Necessary cookies are always allowed as they're required for basic functionality
        return true;
    }

    /**
    * Initialize account management system

    /**
    * Initialize account management system
    * 
    * This function initializes the account management system by checking for
    * existing user sessions, restoring user state from localStorage, and
    * setting up the appropriate UI state. It handles session restoration
    * and automatic dashboard opening for returning users.
    * 
    * Features:
    * - Session restoration from localStorage
    * - User state validation and error handling
    * - Dashboard menu visibility management
    * - Automatic dashboard opening for returning users
    * - Error handling with automatic logout on invalid data
    * 
    * @returns {void} No return value
    */
    function initializeAccountManagement()
    {
        // Load auth token if necessary cookies are allowed
        if (isNecessaryCookiesAllowed())
        {
            authToken = localStorage.getItem('authToken');
        }

        // Check if user is already logged in from previous session
        const storedUser = localStorage.getItem('currentUser');
        if (storedUser && authToken)
        {
            try
            {
                // Restore user state from localStorage
                currentUser = JSON.parse(storedUser);
                updateDashboardMenuVisibility();

                // Auto-open dashboard if user was previously on dashboard
                const wasOnDashboard = localStorage.getItem('wasOnDashboard') === 'true';
                if (wasOnDashboard)
                {
                    setTimeout(() =>
                    {
                        showUserDashboard();
                    }, 1000); // Wait for page to fully load
                }
            } catch ($2)
            {
                console.error('Error parsing stored user data:', error);
                // Clear invalid data and logout user
                currentUser = null;
                authToken = null;
                localStorage.removeItem('authToken');
                localStorage.removeItem('currentUser');
                localStorage.removeItem('wasOnDashboard');
                updateDashboardMenuVisibility();
            }
        }
        else
        {
            // No user logged in - ensure UI is updated to hide username label
            updateDashboardMenuVisibility();
        }
    }

    /**
    * Update dashboard menu visibility based on user login status
    */
    function updateDashboardMenuVisibility()
    {
        // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('updateDashboardMenuVisibility called');
        console.log('currentUser:', currentUser);
        console.log('authToken present:', !!authToken);

        const dashboardItem = document.getElementById('navDashboardItem');
        const mobileDashboardItem = document.getElementById('mobileNavDashboardItem');

        console.log('dashboardItem found:', !!dashboardItem);
        console.log('mobileDashboardItem found:', !!mobileDashboardItem);

        if (currentUser && authToken)
        {
            // User is logged in - show dashboard
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('User is logged in, showing dashboard menu items');
            if (dashboardItem)
            {
                dashboardItem.style.display = 'block';
                // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Desktop dashboard item shown');
            }
            if (mobileDashboardItem)
            {
                mobileDashboardItem.style.display = 'block';
                // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Mobile dashboard item shown');
            }
        }
        else
        {
            // User is not logged in - hide dashboard
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('User is not logged in, hiding dashboard menu items');
            if (dashboardItem)
            {
                dashboardItem.style.display = 'none';
            }
            if (mobileDashboardItem)
            {
                mobileDashboardItem.style.display = 'none';
            }
        }

        // Update user name display
        updateUserNameDisplay();
    }

    /**
    * Update dashboard header based on user role
    */
    function updateDashboardHeader()
    {
        const dashboardHeader = document.querySelector('.dashboard-header h1');
        if (dashboardHeader && currentUser)
        {
            if (currentUser.role === 'admin')
            {
                dashboardHeader.textContent = 'Admin Dashboard';
            }
            else
            {
                dashboardHeader.textContent = 'Mein Dashboard';
            }
        }
    }

    /**
    * Update user name display next to login icon
    */
    function updateUserNameDisplay()
    {
        const userNameElement = document.getElementById('userName');

        if (currentUser && currentUser.name)
        {
            const greetingText = `Hallo ${currentUser.name}`;

            // Update the user name element (next to the login button)
            if (userNameElement)
            {
                userNameElement.textContent = greetingText;
                userNameElement.style.display = 'block';
            }
        }
        else
        {
            // Hide the element when user is not logged in
            if (userNameElement)
            {
                // Use setProperty with important to override CSS !important rules
                userNameElement.style.setProperty('display', 'none', 'important');
            }
        }
    }

    /**
    * Ensure navigation menu is visible (fix for mobile detection issues)
    */
    function ensureNavigationVisible()
    {
        // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('ensureNavigationVisible called');

        const navLinks = document.querySelector('.nav-links');
        const mobileMenuToggle = document.querySelector('.mobile-menu-toggle');

        if (navLinks)
        {
            // Force desktop navigation to be visible
            navLinks.style.display = 'flex';
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Desktop navigation forced to be visible');
        }

        if (mobileMenuToggle)
        {
            // Hide mobile toggle on desktop
            mobileMenuToggle.style.display = 'none';
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Mobile toggle hidden');
        }

        // Also ensure header is visible
        const header = document.querySelector('header');
        if (header)
        {
            header.style.display = 'block';
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Header ensured to be visible');
        }

        // Additional safety: ensure page scroll is unlocked
        if (window.UIUtils && window.UIUtils.unlockPageScroll)
        {
            window.UIUtils.unlockPageScroll();
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔓 Page scroll unlocked as safety measure');
        }
    }

    /**
    * Toggle user menu
    */
    function toggleUserMenu()
    {
        // Remove any existing modals first
        const existingModal = document.getElementById('userModal');
        if (existingModal)
        {
            existingModal.remove();
            // Update global close button
            if (window.UIUtils && window.UIUtils.updateGlobalCloseButton) {
                window.UIUtils.updateGlobalCloseButton();
            }
            if (window.UIUtils && window.UIUtils.unlockPageScroll)
            {
                window.UIUtils.unlockPageScroll();
            }
            return;
        }

        const modal = document.createElement('div');
        modal.id = 'userModal';
        // Check if mobile device
        const isMobile = window.innerWidth <= 768;
        
        modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: ${isMobile ? '#000000' : 'rgba(0,0,0,0.8)'}; /* Fully opaque on mobile to hide content below */
        z-index: ${isMobile ? '10001' : '11000'}; /* Above header (10000) on desktop */
        display: flex;
        align-items: center; /* Centered on mobile */
        justify-content: center;
        padding: ${isMobile ? '1rem' : '2rem'};
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        `;

        const modalContent = document.createElement('div');
        modalContent.style.cssText = `
        background: white;
        border-radius: 15px;
        padding: ${isMobile ? '1rem' : '2rem'};
        max-width: ${isMobile ? 'calc(100vw - 2rem)' : '500px'};
        width: 95%;
        max-height: ${isMobile ? '90vh' : '90vh'}; /* Use full viewport height on mobile */
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        position: relative;
        `;

        if (currentUser)
        {
            // Show user info and logout option
            modalContent.innerHTML = `
            <h2 style="color: #667eea; margin-bottom: 1rem;">Willkommen, ${currentUser.name}!</h2>
            <div style="margin-bottom: 1.5rem;">
            <p style="color: #666; margin-bottom: 0.5rem;"><strong>E-Mail:</strong> ${currentUser.email}</p>
            </div>
            <div style="display: flex; gap: 1rem; flex-wrap: wrap;">
            <button onclick="showUserDashboard(); closeModal('userModal');" style="background: #667eea; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; flex: 1;">Dashboard</button>
            <button onclick="logout(); closeModal('userModal');" style="background: #dc3545; color: white; border: none; padding: 12px 24px; border-radius: 8px; cursor: pointer; flex: 1;">Abmelden</button>
            </div>
            `;
        }
        else
        {
            // Show login/register form
            modalContent.innerHTML = `
            <div id="loginForm">
            <h2 style="color: #667eea; margin-bottom: 1rem;">Anmelden</h2>
            <form onsubmit="handleLogin(event)">
            <div style="margin-bottom: 1rem;">
            <label style="display: block; margin-bottom: 0.5rem; color: #333;">E-Mail:</label>
            <input type="email" id="loginEmail" required style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;" oninput="toggleForgotPasswordButton()">
            </div>
            <div style="margin-bottom: 1.5rem;">
            <label style="display: block; margin-bottom: 0.5rem; color: #333;">Passwort:</label>
            <div style="position: relative; width: 100%;">
            <input type="password" id="loginPassword" required style="width: 100%; padding: 10px 45px 10px 10px; border: 1px solid #ddd; border-radius: 5px; box-sizing: border-box;">
            <button type="button" id="togglePassword" onclick="togglePasswordVisibility('loginPassword')" style="position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; font-size: 18px; color: #666; padding: 4px; display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; z-index: 10;">👁️</button>
            </div>
            </div>
            <button type="submit" style="width: 100%; background: #667eea; color: white; border: none; padding: 12px; border-radius: 8px; cursor: pointer; margin-bottom: 1rem;">Anmelden</button>
            </form>
            <div style="text-align: center; margin-top: 1rem; display: flex; align-items: center; justify-content: center; gap: 8px;">
            <button type="button" id="forgotPasswordBtn" onclick="handleForgotPassword()" disabled style="background: none; border: none; color: #ccc; cursor: not-allowed; text-decoration: underline; font-size: 0.9rem;">Passwort vergessen?</button>
            <button type="button" id="forgotPasswordInfoBtn" onclick="showForgotPasswordInfo()" style="background: none; border: none; color: #999; cursor: help; font-size: 0.8rem; padding: 2px 4px; border-radius: 50%; width: 18px; height: 18px; display: flex; align-items: center; justify-content: center;">ℹ️</button>
            </div>
            </div>
            `;
        }

        modal.appendChild(modalContent);
        document.body.appendChild(modal);
        
        // Register with global close button system - use requestAnimationFrame to ensure modal is fully rendered
        if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
        {
            requestAnimationFrame(() => {
                window.UIUtils.updateGlobalCloseButton();
            });
        }

        // Lock page scroll when modal is open
        if (window.UIUtils && window.UIUtils.lockPageScroll)
        {
            window.UIUtils.lockPageScroll();
        }
    }


    /**
    * Handle forgot password functionality
    */
    async function handleForgotPassword()
    {
        // Prevent duplicate submissions
        if (passwordResetInProgress)
        {
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Passwort-Reset wird bereits verarbeitet. Bitte warten Sie...', 'info');
            }
            return;
        }

        const email = document.getElementById('loginEmail')?.value;

        if (!email)
        {
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Bitte geben Sie Ihre E-Mail-Adresse ein', 'warning');
            }
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email))
        {
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Bitte geben Sie eine gültige E-Mail-Adresse ein', 'warning');
            }
            return;
        }

        // Set flag to prevent duplicate requests
        passwordResetInProgress = true;

        // Disable the button to prevent multiple clicks
        const forgotPasswordBtn = document.getElementById('forgotPasswordBtn');
        if (forgotPasswordBtn)
        {
            forgotPasswordBtn.disabled = true;
            const originalText = forgotPasswordBtn.textContent;
            forgotPasswordBtn.textContent = 'Wird gesendet...';
        }

        try
        {
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Passwort wird an Ihre E-Mail gesendet...', 'info');
            }

            const response = await fetch('/api/forgot-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email })
            });

            const data = await response.json();

            if (response.ok)
            {
                if (window.UIUtils)
                {
                    window.UIUtils.showNotification('Passwort wurde an Ihre E-Mail gesendet', 'success');
                }
            }
            else
            {
                // Check if this is an admin user trying to reset password
                if (response.status === 403 && data.isAdmin && data.requires2FA)
                {
                    if (window.UIUtils)
                    {
                        window.UIUtils.showNotification(
                        'Admin password reset requires 2FA verification. Please use the admin dashboard to change your password or contact the system administrator.', 
                        'warning'
                        );
                    }
                }
                else
                {
                    if (window.UIUtils)
                    {
                        window.UIUtils.showNotification(data.error || 'Fehler beim Senden des Passworts', 'error');
                    }
                }
            }
        } catch (error)
        {
            console.error('Forgot password error:', error);
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Fehler beim Senden des Passworts', 'error');
            }
        }
        finally
        {
            // Reset flag and re-enable button
            passwordResetInProgress = false;
            if (forgotPasswordBtn)
            {
                forgotPasswordBtn.disabled = false;
                forgotPasswordBtn.textContent = 'Passwort vergessen?';
            }
        }
    }

    // Removed duplicate togglePasswordVisibility function - using the generic version below

    /**
    * Handle user login with authentication and session management
    * 
    * This function handles user login by sending credentials to the server,
    * processing the authentication response, and managing user session state.
    * It includes comprehensive error handling and automatic dashboard opening
    * after successful login.
    * 
    * Features:
    * - Form validation and submission handling
    * - Server authentication with proper headers
    * - Session state management (localStorage)
    * - Automatic dashboard opening after login
    * - Comprehensive error handling
    * - User feedback through notifications
    * - Modal management
    * 
    * @param {Event} event - Form submit event from the login form
    * @returns {Promise<void>} Promise that resolves when login process completes
    */
    async function handleLogin(event)
    {
        event.preventDefault();

        const emailInput = document.getElementById('loginEmail');
        const passwordInput = document.getElementById('loginPassword');
        
        if (!emailInput || !passwordInput) {
            if (window.UIUtils) {
                window.UIUtils.showNotification('Login-Formular nicht gefunden', 'error');
            }
            return;
        }
        
        const email = emailInput.value.trim();
        const password = passwordInput.value.trim();
        
        if (!email || !password) {
            if (window.UIUtils) {
                window.UIUtils.showNotification('Bitte geben Sie E-Mail und Passwort ein', 'error');
            }
            return;
        }
        
        // Store password in a variable that won't be lost during async operations
        const storedPassword = password;
        
        try
        {
            // Send login request to server
            const response = await fetch('/api/login', 
            {
                method: 'POST',
                headers: 
                {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password })
            });

            const data = await response.json();

            if (response.ok)
            {
                // Check if user is admin and requires verification (only in production Docker mode)
                if (data.user && data.user.role === 'admin')
                {
                    // Check if 2FA is required by calling server endpoint
                    try
                    {
                        const twoFactorResponse = await fetch('/api/admin/check-2fa-required', {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                            }
                        });

                        const twoFactorData = await twoFactorResponse.json();
                        const requires2FA = twoFactorData.requires2FA;

                        if (requires2FA)
                        {
                            // Admin login requires verification in production Docker
                            const modal = document.getElementById('userModal');
                            if (modal)
                            {
                                modal.remove();
                            }

                            // Wait for AdminVerification to be available or trigger dashboard script loading
                            if (!window.AdminVerification) {
                                // Try to trigger dashboard script loading if available
                                if (typeof loadDashboardScripts === 'function') {
                                    loadDashboardScripts();
                                }
                                // Wait up to 3 seconds for AdminVerification to load
                                let waitTime = 0;
                                while (!window.AdminVerification && waitTime < 3000) {
                                    await new Promise(resolve => setTimeout(resolve, 100));
                                    waitTime += 100;
                                }
                            }
                            
                            if (window.AdminVerification)
                            {
                                await window.AdminVerification.startAdminVerification(email);
                            }
                            else
                            {
                                console.error('window.AdminVerification is not available after waiting!');
                                if (window.UIUtils)
                                {
                                    window.UIUtils.showNotification('Admin-Verifizierungssystem nicht verfügbar. Bitte Seite neu laden.', 'error');
                                }
                            }
                            return;
                        }
                        else
                        {
                            // 2FA is disabled via ENABLE_2FA=false environment variable
                            // Allow direct admin login without verification
                            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔓 2FA disabled: Admin login without verification');
                        }
                    } catch ($2)
                    {
                        console.error('Error checking 2FA requirement:', error);
                        // Default to requiring 2FA if check fails (safer)
                        const modal = document.getElementById('userModal');
                        if (modal)
                        {
                            modal.remove();
                        }

                        // Unlock page scroll when modal is removed
                        if (window.UIUtils && window.UIUtils.unlockPageScroll)
                        {
                            window.UIUtils.unlockPageScroll();
                            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔓 Page scroll unlocked after 2FA error');
                        }

                        // Wait for AdminVerification to be available or trigger dashboard script loading
                        if (!window.AdminVerification) {
                            // Try to trigger dashboard script loading if available
                            if (typeof loadDashboardScripts === 'function') {
                                loadDashboardScripts();
                            }
                            // Wait up to 3 seconds for AdminVerification to load
                            let waitTime = 0;
                            while (!window.AdminVerification && waitTime < 3000) {
                                await new Promise(resolve => setTimeout(resolve, 100));
                                waitTime += 100;
                            }
                        }
                        
                        if (window.AdminVerification)
                        {
                            await window.AdminVerification.startAdminVerification(email);
                        }
                        else
                        {
                            if (window.UIUtils)
                            {
                                window.UIUtils.showNotification('Admin-Verifizierungssystem nicht verfügbar', 'error');
                            }
                        }
                        return;
                    }
                }

                // Regular user login (non-admin)
                // Store user data and token for session management
                currentUser = data.user;
                authToken = data.token;
                localStorage.setItem('authToken', authToken);
                localStorage.setItem('currentUser', JSON.stringify(currentUser));

                // Update UI state and close login modal
                updateDashboardMenuVisibility();

                // Ensure modal is properly closed
                const modal = document.getElementById('userModal');
                if (modal)
                {
                    modal.remove();
                }

                // Unlock page scroll after successful login
                if (window.UIUtils && window.UIUtils.unlockPageScroll)
                {
                    window.UIUtils.unlockPageScroll();
                    // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔓 Page scroll unlocked after successful login');
                }

                // Automatically open dashboard after successful login
                setTimeout(() =>
                {
                    showUserDashboard();
                }, 500);

                if (window.UIUtils)
                {
                    window.UIUtils.showNotification('Erfolgreich angemeldet!', 'success');
                }
            }
            else
            {
                // Handle login failure
                if (window.UIUtils)
                {
                    window.UIUtils.showNotification(data.error || 'Anmeldung fehlgeschlagen', 'error');
                }
            }
        } catch ($2)
        {
            console.error('Login error:', error);
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Verbindungsfehler bei der Anmeldung', 'error');
            }
        }
    }

    /**
    * Logout user
    */
    function logout()
    {
        // Clear user data first
        currentUser = null;
        authToken = null;
        localStorage.removeItem('currentUser');
        localStorage.removeItem('authToken');
        localStorage.removeItem('wasOnDashboard');

        // Close any existing modals immediately
        const existingModal = document.getElementById('userModal') || 
        document.getElementById('dashboardModal') || 
        document.getElementById('orderHistoryModal') || 
        document.getElementById('carDetailsModal');
        if (existingModal)
        {
            existingModal.remove();
        }

        // If dashboard is open, close it immediately
        if (window.UIUtils && window.UIUtils.isDashboardOpen())
        {
            window.UIUtils.hideDashboard();
        }

        // Update UI state to hide dashboard menu items
        updateDashboardMenuVisibility();

        // Hide any login modal that might appear
        const loginModal = document.getElementById('loginModal');
        if (loginModal)
        {
            loginModal.style.display = 'none';
            loginModal.remove();
        }

        // Show logout notification (brief)
        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Erfolgreich abgemeldet', 'info');
        }

        // Scroll to Leistungen section
        setTimeout(() => {
            const servicesSection = document.getElementById('services');
            if (servicesSection)
            {
                const headerHeight = window.UIUtils && window.UIUtils.getHeaderHeight 
                ? window.UIUtils.getHeaderHeight() 
                : 100;
                const targetPosition = servicesSection.offsetTop - headerHeight - 20;
                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth'
                });
            }
        }, 300);

        // Reload the page after a short delay to ensure UI updates
        setTimeout(() => {
            window.location.reload();
        }, 2000);
    }

    /**
    * Show user dashboard
    */
    async function showUserDashboard()
    {
        // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('showUserDashboard called');
        console.log('Current user:', currentUser);
        console.log('Auth token present:', !!authToken);
        console.log('UIUtils available:', !!window.UIUtils);
        console.log('UIUtils.showDashboard available:', !!(window.UIUtils && window.UIUtils.showDashboard));

        if (!currentUser || !authToken)
        {
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('User not authenticated, showing warning');
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Bitte melden Sie sich zuerst an', 'warning');
            }
            toggleUserMenu();
            return;
        }

        // Use UIUtils to show dashboard
        if (window.UIUtils && window.UIUtils.showDashboard)
        {
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Using UIUtils.showDashboard');
            window.UIUtils.showDashboard();

            // Verify dashboard is actually visible after showing
            setTimeout(() => {
                const dashboardSection = document.getElementById('dashboard');
                if (dashboardSection)
                {
                    console.log('Dashboard section display after showDashboard:', dashboardSection.style.display);
                    console.log('Dashboard section computed display:', window.getComputedStyle(dashboardSection).display);
                }
            }, 100);
        }
        else
        {
            console.warn('UIUtils.showDashboard not available, using fallback');
            // Fallback: directly show dashboard section
            const dashboardSection = document.getElementById('dashboard');
            if (dashboardSection)
            {
                // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Showing dashboard section directly');
                dashboardSection.style.display = 'block';
                // Hide other sections
                const sections = ['home', 'uber', 'services', 'webshop', 'contact'];
                sections.forEach(sectionId =>
                {
                    const section = document.getElementById(sectionId);
                    if (section)
                    {
                        section.style.display = 'none';
                    }
                });

                // Verify dashboard is visible
                setTimeout(() => {
                    console.log('Dashboard section display after fallback:', dashboardSection.style.display);
                    console.log('Dashboard section computed display:', window.getComputedStyle(dashboardSection).display);
                }, 100);
            }
            else
            {
                console.error('Dashboard section not found!');
            }
        }

        // Update dashboard header based on user role
        updateDashboardHeader();

        // Ensure navigation menu is visible (fix for mobile detection issues)
        ensureNavigationVisible();

        // Force dashboard to stay visible (additional safety check)
        setTimeout(() => {
            const dashboardSection = document.getElementById('dashboard');
            if (dashboardSection && dashboardSection.style.display !== 'block')
            {
                // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔧 Force showing dashboard - was hidden by other code');
                dashboardSection.style.display = 'block';
            }
        }, 200);

        // Track that user is on dashboard for auto-resume
        localStorage.setItem('wasOnDashboard', 'true');

        // Load dashboard content
        const dashboardContent = document.getElementById('dashboardMainContent');
        if (!dashboardContent)
        {
            console.error('Dashboard content container not found!');
            return;
        }

        // Show loading state
        dashboardContent.innerHTML = `
        <div style="text-align: center; padding: 2rem;">
        <div style="display: inline-block; width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #667eea; border-radius: 50%; animation: spin 1s linear infinite;"></div>
        <p style="margin-top: 1rem; color: #666;">Dashboard wird geladen...</p>
        </div>
        `;
        try
        {
            // Load user profile data
            const response = await fetch('/api/profile', {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });

            if (!response.ok)
            {
                throw new Error('Fehler beim Laden des Profils');
            }

            const profileData = await response.json();

            // Load dashboard based on user role
            if (currentUser.role === 'admin')
            {
                if (window.AdminDashboard)
                {
                    window.AdminDashboard.loadAdminDashboard();
                }
                else
                {
                    dashboardContent.innerHTML = '<p>Admin Dashboard wird geladen...</p>';
                }
            }
            else
            {
                // For customer users, use the dashboard.js flow to generate dynamic content
                // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔍 [LOGIN DEBUG] Loading customer dashboard for role:', currentUser.role);
                // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔍 [LOGIN DEBUG] Dashboard available:', !!window.Dashboard);

                if (window.Dashboard)
                {
                    // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔍 [LOGIN DEBUG] Calling Dashboard.loadCustomerDashboard()');
                    // Pass the current user data to the dashboard
                    window.Dashboard.loadCustomerDashboard(currentUser);
                }
                else
                {
                    console.error('❌ [LOGIN DEBUG] Dashboard not available!');
                    dashboardContent.innerHTML = '<p>Kontrollzentrum wird geladen...</p>';
                }
            }
        } catch ($2)
        {
            console.error('Error loading dashboard:', error);
            dashboardContent.innerHTML = `
            <div style="text-align: center; padding: 2rem;">
            <p style="color: #dc3545;">Fehler beim Laden des Dashboards: ${error.message}</p>
            <button onclick="showUserDashboard()" style="background: #667eea; color: white; border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer; margin-top: 1rem;">Erneut versuchen</button>
            </div>
            `;
        }
    }

    /**
    * Load user dashboard content
    * @param {Object} user - User data
    */
    async function loadUserDashboard(user)
    {
        const dashboardContent = document.getElementById('dashboardMainContent');

        // Check if user has @Home orders
        let hasAtHome = false;
        try
        {
            const authToken = window.AccountManagement ? window.AccountManagement.getAuthToken() : null;
            if (authToken)
            {
                const response = await fetch('/api/user/has-athome', {
                    headers: { 'Authorization': `Bearer ${authToken}` }
                });
                if (response.ok)
                {
                    const data = await response.json();
                    hasAtHome = data.hasAtHome;
                }
            }
        } catch ($2)
        {
            console.error('Error checking @Home orders:', error);
            // Continue with dashboard loading even if check fails
        }

        dashboardContent.innerHTML = `
        <div style="max-width: 1200px; margin: 0 auto; padding: 2rem;">
        <h1 style="color: #333; margin-bottom: 2rem;">Willkommen, ${user.name}!</h1>

        <!-- Tab Navigation -->
        <div style="background: white; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); overflow: hidden;">
        <div style="display: flex; background: transparent; border-bottom: 1px solid #e9ecef;">
        <button id="tab-bestellhistorie" onclick="switchDashboardTab('bestellhistorie')" style="flex: 1; background: transparent; color: #666; border: none; padding: 1rem; cursor: pointer; font-weight: 600; transition: background-color 0.3s ease;">📋 Bestellhistorie</button>
        <button id="tab-profile" onclick="switchDashboardTab('profile')" style="flex: 1; background: transparent; color: #666; border: none; padding: 1rem; cursor: pointer; font-weight: 600; transition: background-color 0.3s ease;">👤 Profil-Informationen</button>
        ${hasAtHome ? `
            <button id="tab-downloads" onclick="switchDashboardTab('downloads')" style="flex: 1; background: transparent; color: #666; border: none; padding: 1rem; cursor: pointer; font-weight: 600; transition: background-color 0.3s ease;">📁 Downloads</button>
            ` : ''}
        </div>

        <!-- Tab Content Container -->
        <div id="dashboard-tab-content" style="padding: 2rem;">
        <!-- Tab content will be loaded here -->
        </div>
        </div>

        </div>
        `;

        // Load initial tab content (Bestellhistorie)
        await switchDashboardTab('bestellhistorie');
    }

    /**
    * Switch dashboard tabs
    * @param {string} tabName - Name of the tab to switch to
    */
    async function switchDashboardTab(tabName)
    {
        // Update tab buttons appearance
        const tabs = ['tab-bestellhistorie', 'tab-profile', 'tab-downloads'];
        tabs.forEach(tabId => {
            const tabElement = document.getElementById(tabId);
            if (tabElement)
            {
                const isActive = tabElement.id === `tab-${tabName}`;
                tabElement.style.background = isActive ? '#667eea' : 'transparent';
                tabElement.style.color = isActive ? 'white' : '#666';
            }
        });

        // Get current user data for tabs that need it
        const currentUserData = currentUser;
        if (!currentUserData) return;

        const tabContent = document.getElementById('dashboard-tab-content');
        if (!tabContent) return;

        let content = '';

        switch (tabName)
        {
            case 'bestellhistorie':
            try
            {
                // Load orders for the current user
                const authToken = window.AccountManagement ? window.AccountManagement.getAuthToken() : null;
                const currentUser = window.AccountManagement ? window.AccountManagement.getCurrentUser() : null;
                console.log('Current user:', currentUser);
                console.log('Auth token:', authToken);

                if (!authToken)
                {
                    content = `
                    <div style="text-align: center; padding: 2rem;">
                    <p style="color: #dc3545;">Keine Berechtigung zum Laden der Bestellungen.</p>
                    </div>
                    `;
                    break;
                }

                console.log('Attempting to fetch orders with token:', authToken ? 'Present' : 'Missing');
                console.log('Token value:', authToken);
                const response = await fetch('/api/orders', {
                    headers: { 'Authorization': `Bearer ${authToken}` }
                });

                if (!response.ok)
                {
                    const errorText = await response.text();
                    console.error('Orders API failed:', response.status, response.statusText, errorText);
                    throw new Error(`Fehler beim Laden der Bestellungen (${response.status}): ${response.statusText}`);
                }

                const ordersData = await response.json();
                const orders = ordersData.orders || [];

                if (orders.length === 0)
                {
                    content = `
                    <div>
                    <h3 style="color: #333; margin: 0 0 1.5rem 0;">📋 Bestellhistorie</h3>
                    <div style="text-align: center; padding: 3rem; background: #f8f9fa; border-radius: 8px;">
                    <p style="color: #666; margin: 0; font-size: 1.1rem;">Sie haben noch keine Bestellungen.</p>
                    <p style="color: #999; margin: 0.5rem 0 0 0;">Besuchen Sie unseren Konfigurator, um Ihre erste Bestellung zu tätigen.</p>
                    </div>
                    </div>
                    `;
                }
                else
                {
                    // Format orders list
                    const ordersList = orders.map(order => `
                    <div style="background: #f8f9fa; border-radius: 8px; padding: 1.5rem; margin-bottom: 1rem; border-left: 4px solid #667eea;">
                    <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1rem;">
                    <div>
                    <h4 style="margin: 0 0 0.5rem 0; color: #333;">Bestellung #${order.id}</h4>
                    <p style="margin: 0.25rem 0; color: #666;"><strong>Datum:</strong> ${new Date(order.date).toLocaleDateString('de-DE')}</p>
                    <p style="margin: 0.25rem 0; color: #666;"><strong>Status:</strong> ${order.status}</p>
                    <p style="margin: 0.25rem 0; color: #666;"><strong>Gesamt:</strong> €${order.total.toFixed(2)}</p>
                    ${order.paypal_order_id ? '<p style="margin: 0.25rem 0; color: #666;"><strong>PayPal ID:</strong> ' + order.paypal_order_id.substring(0, 20) + '...</p>' : ''}
                    ${order.shipping_tracking_id ? '<p style="margin: 0.25rem 0; color: #17a2b8;"><strong>📦 Sendungsverfolgung:</strong> ' + order.shipping_tracking_id + '</p>' : ''}
                    </div>
                    <div style="display: flex; gap: 0.5rem; flex-direction: column;">
                    ${order.pdf_path ? `
                        <button onclick="downloadUserOrderInvoice('${order.order_id}')" style="background: #17a2b8; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 0.9rem;">📄 Rechnung herunterladen</button>
                        ` : `
                        <span style="background: #6c757d; color: white; border: none; padding: 8px 16px; border-radius: 4px; text-align: center; font-size: 0.9rem; opacity: 0.6;">📄 Rechnung nicht verfügbar</span>
                        `}
                    ${order.shipping_tracking_id ? `
                        <button onclick="trackShipment('${order.shipping_tracking_id}')" style="background: #28a745; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 0.9rem;">📦 Sendung verfolgen</button>
                        ` : ''}
                    </div>
                    </div>
                    ${order.items ? `
                        <div style="background: white; border-radius: 6px; padding: 1rem;">
                        <h5 style="margin: 0 0 0.5rem 0; color: #333;">Bestellte Artikel:</h5>
                        <div style="color: #666;">
                        ${(typeof order.items === 'string' ? JSON.parse(order.items) : order.items).map(item => `
                            <p style="margin: 0.25rem 0;">• ${item.name} (x${item.quantity}) - €${item.price.toFixed(2)}</p>
                            `).join('')}
                        </div>
                        </div>
                        ` : ''}
                    </div>
                    `).join('');

                    content = `
                    <div>
                    <h3 style="color: #333; margin: 0 0 1.5rem 0;">📋 Bestellhistorie (${orders.length} Bestellung${orders.length !== 1 ? 'en' : ''})</h3>
                    <p style="color: #666; margin: 0 0 1.5rem 0;">Hier finden Sie alle Ihre Bestellungen mit Rechnungen zum Download.</p>
                    <div style="max-height: 60vh; overflow-y: auto;">
                    ${ordersList}
                    </div>
                    </div>
                    `;
                }
            } catch ($2)
            {
                console.error('Error loading orders:', error);
                content = `
                <div>
                <h3 style="color: #333; margin: 0 0 1.5rem 0;">📋 Bestellhistorie</h3>
                <div style="text-align: center; padding: 3rem; background: #f8d7da; border-radius: 8px;">
                <p style="color: #721c24; margin: 0;">Fehler beim Laden der Bestellungen: ${error.message}</p>
                </div>
                </div>
                `;
            }
            break;

            case 'profile':
            content = `
            <div>
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;">
            <h3 style="color: #333; margin: 0;">👤 Profil-Informationen</h3>
            <button onclick="showEditProfileModal()" style="background: #667eea; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">✏️ Bearbeiten</button>
            </div>
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1rem;">
            <div>
            <p style="margin: 0.5rem 0;"><strong>Name:</strong> ${currentUserData.name}</p>
            <p style="margin: 0.5rem 0;"><strong>E-Mail:</strong> ${currentUserData.email}</p>
            </div>
            <div>
            <p style="margin: 0.5rem 0;"><strong>Telefon:</strong> ${currentUserData.phone || 'Nicht angegeben'}</p>
            <p style="margin: 0.5rem 0;"><strong>Adresse:</strong> ${currentUserData.address || 'Nicht angegeben'}</p>
            <p style="margin: 0.5rem 0;"><strong>Stadt:</strong> ${currentUserData.city || 'Nicht angegeben'} ${currentUserData.postal_code || ''}</p>
            <p style="margin: 0.5rem 0;"><strong>Land:</strong> ${currentUserData.country || 'Nicht angegeben'}</p>
            </div>
            </div>

            <!-- Password Change Section -->
            <div style="margin-top: 2rem; padding-top: 1.5rem; border-top: 1px solid #e9ecef;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
            <h4 style="color: #333; margin: 0;">🔒 Passwort ändern</h4>
            <button onclick="showChangePasswordModal()" style="background: #28a745; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">Passwort ändern</button>
            </div>
            <p style="color: #666; margin: 0; font-size: 0.9rem;">Ändern Sie Ihr Passwort für zusätzliche Sicherheit.</p>
            </div>
            </div>
            `;
            break;

            case 'downloads':
            content = `
            <div>
            <h3 style="color: #333; margin: 0 0 1.5rem 0;">📁 Downloads</h3>
            <p style="color: #666; margin-bottom: 1.5rem;">Laden Sie die benötigten Dateien für die @Home Installation herunter.</p>

            <div style="background: #f8f9fa; padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem; border-left: 4px solid #17a2b8;">
            <p style="margin: 0; font-size: 0.9rem; color: #0c5460;">
            <strong>ℹ️ Hinweis:</strong> Diese Downloads sind nur für Kunden mit @Home Bestellungen verfügbar.
            Bitte laden Sie alle Dateien herunter, bevor Sie mit der Installation beginnen.
            </p>
            </div>

            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1rem;">
            <div style="background: white; border: 1px solid #e9ecef; border-radius: 8px; padding: 1.5rem; text-align: center;">
            <div style="font-size: 2rem; margin-bottom: 1rem;">📄</div>
            <h4 style="color: #333; margin: 0 0 0.5rem 0;">D-Luft @Home Anleitung (Deutsch)</h4>
            <p style="color: #666; font-size: 0.9rem; margin: 0 0 1rem 0;">Detaillierte Anleitung für die @Home Installation in deutscher Sprache</p>
            <button onclick="downloadFile('Dluft@Home Anleitung.pdf')" style="background: #17a2b8; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; font-weight: 600; width: 100%;">
            📥 Herunterladen (PDF)
            </button>
            </div>

            <div style="background: white; border: 1px solid #e9ecef; border-radius: 8px; padding: 1.5rem; text-align: center;">
            <div style="font-size: 2rem; margin-bottom: 1rem;">📄</div>
            <h4 style="color: #333; margin: 0 0 0.5rem 0;">D-Luft @Home Manual (English)</h4>
            <p style="color: #666; font-size: 0.9rem; margin: 0 0 1rem 0;">Detailed manual for @Home installation in English</p>
            <button onclick="downloadFile('D-Luft @ Home manual English.pdf')" style="background: #17a2b8; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; font-weight: 600; width: 100%;">
            📥 Download (PDF)
            </button>
            </div>

            <div style="background: white; border: 1px solid #e9ecef; border-radius: 8px; padding: 1.5rem; text-align: center;">
            <div style="font-size: 2rem; margin-bottom: 1rem;">💾</div>
            <h4 style="color: #333; margin: 0 0 0.5rem 0;">Tactrix OBD Setup</h4>
            <p style="color: #666; font-size: 0.9rem; margin: 0 0 1rem 0;">Setup-Datei für OpenPort 2.0 OBD-Interface</p>
            <button onclick="downloadFile('openport2_setup_stub.exe')" style="background: #ffc107; color: #212529; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; font-weight: 600; width: 100%;">
            📥 Herunterladen (EXE)
            </button>
            </div>
            </div>

            <div style="background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 8px; padding: 1rem; margin-top: 1.5rem;">
            <p style="margin: 0; font-size: 0.9rem; color: #856404;">
            <strong>⚠️ Wichtiger Hinweis:</strong> Führen Sie die EXE-Datei nur auf einem vertrauenswürdigen Computer aus. 
            Stellen Sie sicher, dass Ihr Antivirus-Programm die Datei nicht blockiert.
            </p>
            </div>
            </div>
            `;
            break;
        }

        tabContent.innerHTML = content;
    }

    /**
    * Download file from server
    * @param {string} filename - Name of the file to download
    */
    async function downloadFile(filename)
    {
        try
        {
            const authToken = getAuthToken();
            if (!authToken)
            {
                if (window.UIUtils)
                {
                    window.UIUtils.showNotification('Keine Berechtigung zum Download', 'error');
                }
                return;
            }

            // Show loading state
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Download wird vorbereitet...', 'info');
            }

            // Create download link
            const downloadUrl = `/api/downloads/${encodeURIComponent(filename)}`;

            // Create a temporary link element to trigger download
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            link.style.display = 'none';

            // Add authorization header by creating a fetch request first
            const response = await fetch(downloadUrl, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });

            if (!response.ok)
            {
                throw new Error('Download fehlgeschlagen');
            }

            // Get the blob and create object URL
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            // Update link with blob URL
            link.href = url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up object URL
            window.URL.revokeObjectURL(url);

            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Download gestartet!', 'success');
            }
        } catch ($2)
        {
            console.error('Download error:', error);
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Fehler beim Download: ' + error.message, 'error');
            }
        }
    }

    /**
    * Track shipment using tracking ID
    * @param {string} trackingId - Tracking ID for the shipment
    */
    function trackShipment(trackingId)
    {
        // Open Deutsche Post tracking in new window
        const trackingUrl = `https://www.deutschepost.de/de/s/sendungsverfolgung.html?piececode=${encodeURIComponent(trackingId)}`;
        window.open(trackingUrl, '_blank', 'noopener,noreferrer');

        if (window.UIUtils)
        {
            window.UIUtils.showNotification('Sendungsverfolgung wird in neuem Fenster geöffnet', 'info');
        }
    }

    /**
    * Show profile edit modal
    */
    async function showEditProfileModal()
    {
        const modal = document.createElement('div');
        modal.id = 'editProfileModal';
        const isMobile = window.innerWidth <= 768;
        modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: ${isMobile ? '#000000' : 'rgba(0,0,0,0.8)'}; /* Fully opaque on mobile to hide content below */
        z-index: ${isMobile ? '10001' : '11000'}; /* Above header (10000) on desktop */
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        `;

        const modalContent = document.createElement('div');
        const isMobileContent = window.innerWidth <= 768;
        modalContent.style.cssText = `
        background: white;
        border-radius: 15px;
        padding: ${isMobileContent ? '1.5rem' : '2rem'};
        max-width: ${isMobileContent ? 'calc(100vw - 1rem)' : '600px'};
        width: ${isMobileContent ? 'calc(100% - 1rem)' : '90%'};
        max-height: 90vh;
        overflow-y: auto;
        overflow-x: hidden;
        box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        position: relative;
        margin: 0 auto;
        box-sizing: border-box;
        `;
        try
        {
            // Get current user data
            const authToken = getAuthToken();
            const response = await fetch('/api/profile', {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });

            if (!response.ok)
            {
                throw new Error('Fehler beim Laden des Profils');
            }

            const profileData = await response.json();
            const user = profileData.user;

            modalContent.innerHTML = `
            <div style="margin-bottom: 1.5rem;">
            <h2 style="color: #667eea; margin: 0;">Profil bearbeiten</h2>
            </div>
            <form onsubmit="updateProfile(event)">
            <div style="margin-bottom: 1rem;">
            <label style="display: block; margin-bottom: 0.5rem; color: #333;">Name:</label>
            <input type="text" id="profileName" value="${user.name}" required style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
            </div>
            <div style="margin-bottom: 1rem;">
            <label style="display: block; margin-bottom: 0.5rem; color: #333;">E-Mail:</label>
            <input type="email" id="profileEmail" value="${user.email}" required style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
            </div>
            <div style="margin-bottom: 1rem;">
            <label style="display: block; margin-bottom: 0.5rem; color: #333;">Telefon:</label>
            <input type="tel" id="profilePhone" value="${user.phone || ''}" pattern="[0-9+\\s\\-\\(\\)]+" title="Nur Zahlen, +, Leerzeichen, Bindestriche und Klammern erlaubt" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
            </div>
            <div style="margin-bottom: 1rem;">
            <label style="display: block; margin-bottom: 0.5rem; color: #333;">Adresse:</label>
            <input type="text" id="profileAddress" value="${user.address || ''}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
            </div>
            <div style="margin-bottom: 1rem;">
            <label style="display: block; margin-bottom: 0.5rem; color: #333;">Stadt:</label>
            <input type="text" id="profileCity" value="${user.city || ''}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
            </div>
            <div style="margin-bottom: 1rem;">
            <label style="display: block; margin-bottom: 0.5rem; color: #333;">Postleitzahl:</label>
            <input type="text" id="profilePostalCode" value="${user.postal_code || ''}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
            </div>
            <div style="margin-bottom: 1rem;">
            <label style="display: block; margin-bottom: 0.5rem; color: #333;">Land:</label>
            <input type="text" id="profileCountry" value="${user.country || ''}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
            </div>
            <div style="margin-bottom: 1.5rem;">
            <label style="display: block; margin-bottom: 0.5rem; color: #333;">Neues Passwort (optional):</label>
            <input type="password" id="profilePassword" placeholder="Passwort ändern..." style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
            <small style="color: #666; font-size: 0.8rem;">Leer lassen, um das Passwort nicht zu ändern</small>
            </div>
            <button type="submit" style="background: #28a745; color: white; border: none; padding: 12px 24px; border-radius: 5px; cursor: pointer; width: 100%; font-weight: 600;">Profil aktualisieren</button>
            </form>
            `;
        } catch ($2)
        {
            modalContent.innerHTML = `
            <div style="text-align: center; padding: 2rem;">
            <h2 style="color: #dc3545;">Fehler beim Laden des Profils</h2>
            <p>${error.message}</p>
            <button onclick="this.closest('#editProfileModal').remove()" style="background: #dc3545; color: white; border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer;">Schließen</button>
            </div>
            `;
        }

        modal.appendChild(modalContent);
        document.body.appendChild(modal);

        // Update global close button
        if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
        {
            requestAnimationFrame(() => {
                window.UIUtils.updateGlobalCloseButton();
            });
        }

        // Lock page scrolling
        if (window.UIUtils && window.UIUtils.lockPageScroll)
        {
            window.UIUtils.lockPageScroll();
        }

        // Handle modal close with scroll unlock
        const closeModal = () =>
        {
            document.body.removeChild(modal);
            if (window.UIUtils && window.UIUtils.unlockPageScroll)
            {
                window.UIUtils.unlockPageScroll();
            }
            // Update global close button after closing
            if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
            {
                window.UIUtils.updateGlobalCloseButton();
            }
        };

        // Close modal when clicking outside
        modal.onclick = (e) =>
        {
            if (e.target === modal)
            {
                closeModal();
            }
        };
    }

    /**
     * Update user profile
     */
    async function updateProfile(event)
    {
        event.preventDefault();

        const name = document.getElementById('profileName').value;
        const email = document.getElementById('profileEmail').value;
        const phone = document.getElementById('profilePhone').value;
        const address = document.getElementById('profileAddress').value;
        const city = document.getElementById('profileCity').value;
        const postal_code = document.getElementById('profilePostalCode').value;
        const country = document.getElementById('profileCountry').value;
        const password = document.getElementById('profilePassword').value;
        try
        {
            const authToken = getAuthToken();
            if (!authToken)
            {
                throw new Error('Keine Berechtigung');
            }

            const response = await fetch('/api/profile', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({ 
                    name, 
                    email, 
                    phone, 
                    address, 
                    city, 
                    postal_code, 
                    country, 
                    password: password.trim() !== '' ? password : undefined 
                })
            });

            const data = await response.json();

            if (response.ok)
            {
                // Close modal using safe close
                const editModal = document.getElementById('editProfileModal');
                if (editModal)
                {
                    if (window.UIUtils && window.UIUtils.safeCloseModal)
                    {
                        window.UIUtils.safeCloseModal('editProfileModal');
                    }
                    else
                    {
                        editModal.remove();
                        if (window.UIUtils && window.UIUtils.unlockPageScroll)
                        {
                            window.UIUtils.unlockPageScroll();
                        }
                    }
                }
                // Refresh dashboard
                showUserDashboard();
                if (window.UIUtils)
                {
                    window.UIUtils.showNotification(`Profil erfolgreich aktualisiert! ${data.changedFields.length > 0 ? 'Änderungen: ' + data.changedFields.join(', ') : ''}`, 'success');
                }
            }
            else
            {
                if (window.UIUtils)
                {
                    window.UIUtils.showNotification('Fehler beim Aktualisieren des Profils: ' + (data.error || 'Unbekannter Fehler'), 'error');
                }
            }
        } catch (error)
        {
            console.error('Fehler beim Aktualisieren des Profils:', error);
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Fehler beim Aktualisieren des Profils', 'error');
            }
        }
    }

    /**
    * Test authentication status
    */
    async function testAuth()
    {
        const token = this.getAuthToken();
        // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('Testing auth...');
        console.log('Token present:', !!token);
        console.log('Current user:', this.getCurrentUser());

        if (!token)
        {
            console.error('No auth token available');
            return;
        }
        try
        {
            const response = await fetch('/api/profile', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            console.log('Profile API status:', response.status);
            if (response.ok)
            {
                const data = await response.json();
                console.log('Profile data:', data);
            }
            else
            {
                const error = await response.text();
                console.error('Profile API error:', error);
            }
        } catch ($2)
        {
            console.error('Test auth error:', error);
        }
    }

    /**
    * Show change password modal
    */
    async function showChangePasswordModal()
    {
        const modal = document.createElement('div');
        modal.id = 'changePasswordModal';
        const isMobile = window.innerWidth <= 768;
        modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: ${isMobile ? '#000000' : 'rgba(0,0,0,0.8)'}; /* Fully opaque on mobile to hide content below */
        z-index: ${isMobile ? '10001' : '11000'}; /* Above header (10000) on desktop */
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        `;

        const modalContent = document.createElement('div');
        const isMobileContent = window.innerWidth <= 768;
        modalContent.style.cssText = `
        background: white;
        border-radius: 15px;
        padding: ${isMobileContent ? '1.5rem' : '2rem'};
        max-width: ${isMobileContent ? 'calc(100vw - 1rem)' : '500px'};
        width: ${isMobileContent ? 'calc(100% - 1rem)' : '90%'};
        max-height: 90vh;
        overflow-y: auto;
        overflow-x: hidden;
        box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        position: relative;
        margin: 0 auto;
        box-sizing: border-box;
        `;

        modalContent.innerHTML = `
        <h2 style="color: #667eea; margin-bottom: 1.5rem;">🔒 Passwort ändern</h2>

        <form onsubmit="handleChangePassword(event)">
        <div style="margin-bottom: 1rem;">
        <label style="display: block; margin-bottom: 0.5rem; color: #333;">Neues Passwort:</label>
        <div style="position: relative; width: 100%;">
        <input type="password" id="newPassword" required minlength="8" style="width: 100%; padding: 10px 45px 10px 10px; border: 1px solid #ddd; border-radius: 5px; box-sizing: border-box;">
        <button type="button" onclick="togglePasswordVisibility('newPassword')" style="position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; font-size: 18px; color: #666; padding: 4px; display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; z-index: 10;">👁️</button>
        </div>
        <small style="color: #666; font-size: 0.8rem;">Mindestens 8 Zeichen</small>
        </div>

        <div style="margin-bottom: 1.5rem;">
        <label style="display: block; margin-bottom: 0.5rem; color: #333;">Passwort bestätigen:</label>
        <div style="position: relative; width: 100%;">
        <input type="password" id="confirmPassword" required minlength="8" style="width: 100%; padding: 10px 45px 10px 10px; border: 1px solid #ddd; border-radius: 5px; box-sizing: border-box;">
        <button type="button" onclick="togglePasswordVisibility('confirmPassword')" style="position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; font-size: 18px; color: #666; padding: 4px; display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; z-index: 10;">👁️</button>
        </div>
        </div>

        <div style="display: flex; gap: 1rem;">
        <button type="button" id="cancelPasswordChangeBtn" style="flex: 1; background: #6c757d; color: white; border: none; padding: 12px; border-radius: 8px; cursor: pointer;">Abbrechen</button>
        <button type="submit" style="flex: 1; background: #28a745; color: white; border: none; padding: 12px; border-radius: 8px; cursor: pointer;">Passwort ändern</button>
        </div>
        </form>
        `;

        modal.appendChild(modalContent);
        document.body.appendChild(modal);

        // Update global close button
        if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
        {
            requestAnimationFrame(() => {
                window.UIUtils.updateGlobalCloseButton();
            });
        }

        // Lock page scrolling
        if (window.UIUtils && window.UIUtils.lockPageScroll)
        {
            window.UIUtils.lockPageScroll();
        }

        // Handle modal close with scroll unlock
        const closeModal = () =>
        {
            document.body.removeChild(modal);
            if (window.UIUtils && window.UIUtils.unlockPageScroll)
            {
                window.UIUtils.unlockPageScroll();
            }
            // Update global close button after closing
            if (window.UIUtils && window.UIUtils.updateGlobalCloseButton)
            {
                window.UIUtils.updateGlobalCloseButton();
            }
        };

        // Close modal when clicking outside
        modal.onclick = (e) =>
        {
            if (e.target === modal)
            {
                closeModal();
            }
        };

        // Attach close handler to cancel button
        const cancelBtn = modalContent.querySelector('#cancelPasswordChangeBtn');
        if (cancelBtn)
        {
            cancelBtn.onclick = (e) => {
                e.preventDefault();
                closeModal();
            };
        }
    }

    /**
     * Handle password change form submission
     */
    async function handleChangePassword(event)
    {
        event.preventDefault();

        // Trim leading and ending spaces from all password fields
        const newPasswordInput = document.getElementById('newPassword');
        const confirmPasswordInput = document.getElementById('confirmPassword');
        
        newPasswordInput.value = newPasswordInput.value.trim();
        confirmPasswordInput.value = confirmPasswordInput.value.trim();
        
        const newPassword = newPasswordInput.value;
        const confirmPassword = confirmPasswordInput.value;

        // Validate passwords match
        if (newPassword !== confirmPassword)
        {
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Die Passwörter stimmen nicht überein', 'error');
            }
            return;
        }

        // Validate password length
        if (newPassword.length < 8)
        {
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Das Passwort muss mindestens 8 Zeichen lang sein', 'error');
            }
            return;
        }
        try
        {
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Passwort wird geändert...', 'info');
            }

            const authToken = window.AccountManagement ? window.AccountManagement.getAuthToken() : null;
            if (!authToken)
            {
                if (window.UIUtils)
                {
                    window.UIUtils.showNotification('Keine Berechtigung zum Ändern des Passworts', 'error');
                }
                return;
            }

            const response = await fetch('/api/change-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({ newPassword })
            });

            const data = await response.json();

            if (response.ok)
            {
                if (window.UIUtils && window.UIUtils.safeCloseModal)
                {
                    window.UIUtils.safeCloseModal('changePasswordModal');
                }
                else
                {
                    document.getElementById('changePasswordModal').remove();
                }
                if (window.UIUtils)
                {
                    window.UIUtils.showNotification('Passwort erfolgreich geändert! Eine Bestätigungs-E-Mail wurde gesendet.', 'success');
                }
            }
            else
            {
                if (window.UIUtils)
                {
                    window.UIUtils.showNotification(data.error || 'Fehler beim Ändern des Passworts', 'error');
                }
            }
        } catch ($2)
        {
            console.error('Change password error:', error);
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Verbindungsfehler beim Ändern des Passworts', 'error');
            }
        }
    }

    /**
     * Toggle password visibility for specific input
     */
    function togglePasswordVisibility(inputId)
    {
        const input = document.getElementById(inputId);
        if (!input)
        {
            console.error('Input element not found:', inputId);
            return;
        }

        // Try multiple strategies to find the toggle button
        let button = null;
        
        // Strategy 1: Try to find button as next sibling (for backward compatibility)
        button = input.nextElementSibling;
        if (button && button.tagName === 'BUTTON' && (button.id && button.id.includes('togglePassword') || button.onclick && button.onclick.toString().includes('togglePasswordVisibility')))
        {
            // Found it!
        }
        else
        {
            // Strategy 2: Look for button with id="togglePassword" in parent container
            const parent = input.parentElement;
            if (parent)
            {
                button = parent.querySelector('button#togglePassword');
            }
            
            // Strategy 3: If still not found, look for any button in parent with onclick containing togglePasswordVisibility
            if (!button && parent)
            {
                const buttons = parent.querySelectorAll('button');
                for (let btn of buttons)
                {
                    // Check both onclick function and onclick attribute
                    const hasOnclick = (btn.onclick && btn.onclick.toString().includes('togglePasswordVisibility')) ||
                                      (btn.getAttribute('onclick') && btn.getAttribute('onclick').includes('togglePasswordVisibility'));
                    if (hasOnclick)
                    {
                        button = btn;
                        break;
                    }
                }
            }
            
            // Strategy 4: Look for button with id containing "togglePassword"
            if (!button && parent)
            {
                button = parent.querySelector('button[id*="togglePassword"]');
            }
        }

        if (!button)
        {
            console.error('Button element not found for input:', inputId);
            console.error('Input parent:', input.parentElement);
            console.error('Available buttons:', input.parentElement ? Array.from(input.parentElement.querySelectorAll('button')).map(b => ({ id: b.id, onclick: b.onclick ? b.onclick.toString().substring(0, 50) : 'none' })) : 'no parent');
            return;
        }

        if (input.type === 'password')
        {
            input.type = 'text';
            button.textContent = '🙈';
            button.title = 'Passwort verbergen';
        }
        else
        {
            input.type = 'password';
            button.textContent = '👁️';
            button.title = 'Passwort anzeigen';
        }
    }

    /**
    * Toggle forgot password button based on email validity
    */
    function toggleForgotPasswordButton()
    {
        const emailInput = document.getElementById('loginEmail');
        const forgotPasswordBtn = document.getElementById('forgotPasswordBtn');
        const forgotPasswordInfoBtn = document.getElementById('forgotPasswordInfoBtn');

        if (!emailInput || !forgotPasswordBtn || !forgotPasswordInfoBtn) return;

        const email = emailInput.value.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isValidEmail = emailRegex.test(email);

        if (isValidEmail)
        {
            forgotPasswordBtn.disabled = false;
            forgotPasswordBtn.style.color = '#667eea';
            forgotPasswordBtn.style.cursor = 'pointer';
            forgotPasswordInfoBtn.style.display = 'none';
        }
        else
        {
            forgotPasswordBtn.disabled = true;
            forgotPasswordBtn.style.color = '#ccc';
            forgotPasswordBtn.style.cursor = 'not-allowed';
            forgotPasswordInfoBtn.style.display = 'flex';
        }
    }

    /**
    * Show info about why forgot password button is disabled
    */
    function showForgotPasswordInfo()
    {
        const emailInput = document.getElementById('loginEmail');
        const email = emailInput ? emailInput.value.trim() : '';

        let message = 'Das "Passwort vergessen?" Feld ist deaktiviert, weil keine gültige E-Mail-Adresse eingegeben wurde.';

        if (!email)
        {
            message = 'Bitte geben Sie zuerst Ihre E-Mail-Adresse ein, um das Passwort zurücksetzen zu können.';
        }
        else
        {
            message = 'Die eingegebene E-Mail-Adresse ist nicht gültig. Bitte geben Sie eine korrekte E-Mail-Adresse ein (z.B. beispiel@email.com).';
        }

        if (window.UIUtils)
        {
            window.UIUtils.showNotification(message, 'info', 5000);
        }
        else
        {
            // Fallback notification if UIUtils is not available
            const notification = document.createElement('div');
            notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #17a2b8;
            color: white;
            padding: 12px 20px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            z-index: 10000;
            max-width: 400px;
            word-wrap: break-word;
            `;
            // Use textContent (not innerHTML) to prevent any HTML injection, including close buttons
            // All notifications auto-remove after duration - no 'x' button needed
            notification.textContent = message;
            document.body.appendChild(notification);

            // Auto-remove after 5 seconds
            setTimeout(() =>
            {
                if (notification.parentNode)
                {
                    notification.parentNode.removeChild(notification);
                }
            }, 5000);
        }
    }

    /**
    * Close modal by ID
    * @param {string} modalId - ID of the modal to close
    */
    function closeModal(modalId)
    {
        if (window.UIUtils && window.UIUtils.safeCloseModal)
        {
            window.UIUtils.safeCloseModal(modalId);
        }
        else
        {
            // Fallback if UIUtils is not available
            const modal = document.getElementById(modalId);
            if (modal)
            {
                modal.remove();
            }
        }
    }

    // Export functions for use in other modules
    window.AccountManagement = {
        initializeAccountManagement,
        updateDashboardMenuVisibility,
        updateDashboardHeader,
        updateUserNameDisplay,
        ensureNavigationVisible,
        toggleUserMenu,
        showLoginForm: toggleUserMenu, // Alias for compatibility with dashboard.js
        togglePasswordVisibility,
        handleLogin,
        handleForgotPassword,
        logout,
        showUserDashboard,
        loadUserDashboard,
        switchDashboardTab,
        showChangePasswordModal,
        handleChangePassword,
        toggleForgotPasswordButton,
        showForgotPasswordInfo,
        getCurrentUser: () => currentUser,
        getAuthToken: () => authToken,
        setCurrentUser: (user) =>
        {
            currentUser = user;
        },
        setAuthToken: (token) =>
        {
            authToken = token;
        },
        testAuth,
        downloadFile,
        trackShipment
    };

    // Listen for order assignment notifications to refresh customer dashboard
    window.addEventListener('orderAssigned', function(event) {
        const { userId, timestamp } = event.detail;

        // Check if the current user is the one who got an order assigned
        if (currentUser && currentUser.id === userId)
        {
            // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔄 Order assigned to current user, refreshing dashboard...');

            // Refresh the orders tab if it's currently active
            const currentTab = document.querySelector('.dashboard-tab.active');
            if (currentTab && currentTab.id === 'tab-bestellhistorie')
            {
                // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔄 Refreshing orders tab...');
                switchDashboardTab('bestellhistorie');
            }

            // Show a notification to the user
            if (window.UIUtils)
            {
                window.UIUtils.showNotification('Eine neue Bestellung wurde Ihrem Konto zugewiesen!', 'info');
            }
        }
    });

    // Listen for postMessage events from parent windows (for iframe scenarios)
    window.addEventListener('message', function(event) {
        if (event.data && event.data.type === 'ORDER_ASSIGNED')
        {
            const { userId, timestamp } = event.data;

            // Check if the current user is the one who got an order assigned
            if (currentUser && currentUser.id === userId)
            {
                // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔄 Order assigned to current user (from parent window), refreshing dashboard...');

                // Refresh the orders tab if it's currently active
                const currentTab = document.querySelector('.dashboard-tab.active');
                if (currentTab && currentTab.id === 'tab-bestellhistorie')
                {
                    // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: // Debug: console.log('🔄 Refreshing orders tab...');
                    switchDashboardTab('bestellhistorie');
                }

                // Show a notification to the user
                if (window.UIUtils)
                {
                    window.UIUtils.showNotification('Eine neue Bestellung wurde Ihrem Konto zugewiesen!', 'info');
                }
            }
        }
    });

    // Make functions used in HTML templates globally available
    window.toggleForgotPasswordButton = toggleForgotPasswordButton;
    window.showForgotPasswordInfo = showForgotPasswordInfo;
    window.handleLogin = handleLogin;
    window.handleForgotPassword = handleForgotPassword;
    window.handleChangePassword = handleChangePassword;
    window.togglePasswordVisibility = togglePasswordVisibility;

    // Initialize account management when DOM is ready
    if (document.readyState === 'loading')
    {
        document.addEventListener('DOMContentLoaded', initializeAccountManagement);
    }
    else
    {
        initializeAccountManagement();
    }

})();
